import { axiosGet, axiosPatch, axiosPost, axiosPut } from '@/api/handlers';
import { type UseQueryResult, useMutation, useQuery } from '@/api/hooks';
import type { CascaderOption } from '@/components';
import { getCascaderOptions } from '@/features/project/_utils/option-formatter';
import {
  BulkUpdateExcludedFlagsFormSchema,
  BulkUpdateExcludedFlagsResponseModel,
  type OrganizationFormSchema,
  OrganizationModel,
  OrganizationNestedListModel,
  OrganizationNestedResultsModel,
  OrganizationRecursiveReturnModel,
  OrganizationWithUppersModel,
  OrganizationsModel,
  UpdateOrganizationMemberSchema,
} from '@/features/tenant-manager/organization/_models/organization';
import { useInvalidateQuery } from '@/hooks';
import {
  convertUpperToArray,
  dateTimeAtRequestFormat,
  isTargetYearWithinFiscalYearRange,
} from '@/utils';
import { chunk } from 'lodash-es';
import { z } from 'zod';
import { invalidateListMap, queryKeyMap } from '../../_utils/query-keys';
import {
  getRecursiveOrganizationWithUpperByKey,
  organizationFormValuesParser,
} from '../_utils/organization';

const OrganizationsModelElement = OrganizationsModel.element;

/// //////////////////////////////////////////////////
/// GET
/// /////////////////////////////////////////////////

/// //////////////////////////////////////////////////
/// useOrganization

type OrganizationType = z.infer<typeof OrganizationModel>;

export const useOrganization = <T = OrganizationType>({
  id,
  select,
  enabled = true,
  params,
}: {
  id: string;
  enabled?: boolean;
  select?: (data: OrganizationType) => T;
  params?: OrganizationsParams;
}) => {
  const { historySavedAt, showMigrated, ...restParams } = params ?? {};

  const requestParams = {
    ...restParams,
    datetimeAt: dateTimeAtRequestFormat(historySavedAt ?? undefined),
    ...(!showMigrated && { isMigrated: false }),
  };

  return useQuery({
    queryKey: queryKeyMap.organization(id, requestParams),
    queryFn: async () => {
      const organization = await axiosGet(
        `/api/organizations/${id}/`,
        requestParams,
      );
      return OrganizationModel.parse(organization);
    },
    select,
    enabled: !!id && enabled,
    gcTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
  });
};
export const useOrganizationRecursive = <
  T = z.infer<typeof OrganizationRecursiveReturnModel>,
>({
  id,
  select,
  params,
  enabled = true,
}: {
  id: string;
  params?: OrganizationsParams;
  select?: (data: z.infer<typeof OrganizationRecursiveReturnModel>) => T;
  enabled?: boolean;
}) => {
  return useOrganization({
    id,
    params,
    select: (data) => {
      const organizations = convertUpperToArray(data, []).reverse();
      const parsedOrganization =
        OrganizationRecursiveReturnModel.safeParse(organizations);
      if (!parsedOrganization.success) {
        return undefined;
      }
      return select
        ? select(parsedOrganization.data)
        : (parsedOrganization.data as T);
    },
    enabled: !!id && enabled,
  });
};

/// //////////////////////////////////////////////////
/// useOrganizations

type OrganizationsParams = {
  historySavedAt?: string | null;
  referenceFiscalYear?: string;
  showMigrated?: true;
  showExcludedOptions?: boolean;
  level?: 1 | 2 | 3 | 4;
};

export const useOrganizations = <
  T = Array<z.infer<typeof OrganizationsModelElement>>,
>(props?: {
  params?: OrganizationsParams;
  select?: (data: z.infer<typeof OrganizationsModel>) => T;
  enabled?: boolean;
}) => {
  const { select, enabled = true, params } = props ?? {};

  const { historySavedAt, showMigrated, ...restParams } = params ?? {};

  const requestParams = {
    ...restParams,
    datetimeAt: dateTimeAtRequestFormat(historySavedAt ?? undefined),
    ...(!showMigrated && { isMigrated: false }),
  };
  return useQuery({
    queryKey: queryKeyMap.organizations(requestParams),
    queryFn: async () => {
      const organizations = await axiosGet(
        '/api/organizations/',
        requestParams,
      );
      return OrganizationsModel.parse(organizations);
    },
    select,
    enabled,
    gcTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
  });
};

/// //////////////////////////////////////////////////
/// useOrganizationListRecursive

export const useOrganizationListRecursive = (params: {
  page?: number;
  referenceFiscalYear?: number;
  showMigrated?: true;
  showExcludedOptions?: boolean;
}) => {
  const {
    page = 1,
    referenceFiscalYear,
    showMigrated,
    showExcludedOptions,
  } = params;
  return useQuery({
    queryKey: queryKeyMap.organizationsNestedList(params),
    queryFn: async () => {
      const organizations = await axiosGet('/api/organizations/nested-lists/', {
        page,
        referenceFiscalYear,
        ...(!showMigrated && { isMigrated: false }),
        showExcludedOptions,
      });
      return OrganizationNestedListModel.parse(organizations);
    },
  });
};

// / //////////////////////////////////////////////////
// / useOrganizationWithUppers

type OrganizationWithUppersParams = {
  ids: string[];
};
type OrganizationWithUpperType = z.infer<
  typeof OrganizationWithUppersModel.element
>;
type OrganizationWithUppersSelectOption = {
  referenceFiscalYear?: number;
};

const useOrganizationWithUppers = <T = Array<OrganizationWithUpperType>>({
  params,
  select,
  enabled = true,
  selectOption,
}: {
  params: OrganizationWithUppersParams;
  select?: (data: Array<OrganizationWithUpperType>) => T;
  selectOption?: OrganizationWithUppersSelectOption;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.organizationWithUppers(params),
    queryFn: async () => {
      const MAX_REQUEST_IDS = 50;
      const removeDeduplicatedIds = [...new Set(params.ids)];
      const chunks = chunk(removeDeduplicatedIds, MAX_REQUEST_IDS);

      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const organizationIds = chunk.join(',');
          const res = await axiosGet('/api/organizations/with-uppers/', {
            organizationIds,
          });
          return OrganizationWithUppersModel.parse(res);
        }),
      );
      if (selectOption?.referenceFiscalYear) {
        return results.flat().filter((organization) => {
          return isTargetYearWithinFiscalYearRange({
            targetYear: selectOption.referenceFiscalYear,
            startYear: organization.startYear,
            endYear: organization.endYear,
          });
        });
      }
      return results.flat();
    },
    enabled: enabled && !!params.ids.length,
    select,
    gcTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
  });
};

type FilterKey =
  | keyof Pick<OrganizationWithUpperType, 'id' | 'name'>
  | 'nameWidthId'
  | 'all';

export const useRecursiveOrganizationFilteredByKey = <
  Separator = string | undefined | null,
  Key extends FilterKey = FilterKey,
  U = Key extends 'nameWidthId'
    ? [{ ids: string[]; names: string[] }]
    : Key extends 'all'
      ? OrganizationWithUpperType[]
      : Separator extends string
        ? string[]
        : string[][],
  T = U,
>({
  params,
  select,
  enabled = true,
  options,
  selectOption,
}: {
  params: OrganizationWithUppersParams;
  options: (
    | {
        filterBy: Exclude<Key, 'nameWidthId'>;
        separator: Separator;
      }
    | {
        filterBy: Extract<Key, 'nameWidthId'>;
        separator?: never;
      }
  ) & { reverse?: boolean; ignoreExcluded?: boolean };
  select?: (data: U) => T;
  selectOption?: OrganizationWithUppersSelectOption;
  enabled?: boolean;
}): UseQueryResult<T, unknown> => {
  return useOrganizationWithUppers({
    params,
    selectOption,
    select: (data) => {
      const { reverse, ignoreExcluded, filterBy } = options;

      const formattedData = ignoreExcluded
        ? data.filter((d) => !d.isExcludedFromOptions)
        : data;

      const filteredData = formattedData.map((d) => {
        if (filterBy === 'all') {
          return d;
        }

        if (filterBy === 'nameWidthId') {
          const names = getRecursiveOrganizationWithUpperByKey(d, 'name');
          const ids = getRecursiveOrganizationWithUpperByKey(d, 'id');
          return {
            ids: reverse ? ids.reverse() : ids,
            names: reverse ? names.reverse() : names,
          };
        }

        const dataByKey = getRecursiveOrganizationWithUpperByKey(d, filterBy);
        const formattedData = reverse ? dataByKey.reverse() : dataByKey;
        const separator = options.separator;
        return separator
          ? formattedData.join(separator as string)
          : formattedData;
      });
      return select ? select(filteredData as U) : (filteredData as T);
    },
    enabled,
  });
};

/// //////////////////////////////////////////////////
/// useOrganizationListAllRecursive

const useOrganizationListAllRecursive = <
  T = z.infer<typeof OrganizationNestedResultsModel>,
>(props?: {
  params?: {
    showMigrated?: true;
    referenceFiscalYear?: string;
    page?: number;
    pageSize?: number;
    showExcludedOptions?: boolean;
    snapshotId?: string;
  };
  enabled?: boolean;
  select?: (data: z.infer<typeof OrganizationNestedResultsModel>) => T;
}) => {
  const { enabled = true, params, select } = props ?? {};
  const { showMigrated, ...restParams } = params ?? {};
  return useQuery({
    queryKey: queryKeyMap.organizationsNestedOptions(params),
    queryFn: async () => {
      const organizations = await axiosGet(
        '/api/organizations/nested-options/',
        { ...restParams, ...(!showMigrated && { isMigrated: false }) },
      );
      return OrganizationNestedResultsModel.parse(organizations);
    },
    select,
    gcTime: 60 * 60 * 12 * 1000,
    staleTime: 60 * 60 * 12 * 1000,
    enabled,
  });
};

export const useOrganizationOptionsRecursive = (props?: {
  params?: {
    showMigrated?: true;
    referenceFiscalYear?: string;
    snapshotId?: string;
  };
  enabled?: boolean;
}) => {
  return useOrganizationListAllRecursive<CascaderOption[] | undefined>({
    ...props,
    select: (nestedOrganization) => {
      return getCascaderOptions(nestedOrganization, 'id', 'name', 'descendant');
    },
  });
};

/// //////////////////////////////////////////////////
/// POST
/// /////////////////////////////////////////////////

export const useCreateOrganization = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (value: z.infer<typeof OrganizationFormSchema>) => {
      const { period, upperOrganizationIds, ...payload } =
        organizationFormValuesParser.parse(value);
      return await axiosPost('/api/organizations/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
        upperId: upperOrganizationIds?.at(-1) ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all(
        invalidateListMap.organizations().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
      );
    },
  });
};

export const useBulkUpdateExcludedFlags = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      req: z.infer<typeof BulkUpdateExcludedFlagsFormSchema>,
    ) => {
      const parsed = BulkUpdateExcludedFlagsFormSchema.parse(req);
      const res = await axiosPost(
        '/api/organizations/bulk-update-excluded-flags/',
        {
          isExcludedFromOptions: parsed.operation === 'exclude',
          organizationIds: parsed.organizationIds,
        },
      );
      return BulkUpdateExcludedFlagsResponseModel.parse(res);
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateListMap.organizations().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        ...invalidateListMap.users().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};

/// //////////////////////////////////////////////////
/// PUT
/// /////////////////////////////////////////////////

export const useUpdateOrganizationMember = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (req: z.infer<typeof UpdateOrganizationMemberSchema>) => {
      const { id, userIds } = UpdateOrganizationMemberSchema.parse(req);
      return await axiosPut(`/api/organizations/${id}/users/`, {
        userIds,
      });
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        ...invalidateListMap.organizations().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization(id)),
      ]);
    },
  });
};

/// //////////////////////////////////////////////////
/// PATCH
/// /////////////////////////////////////////////////

export const useUpdateOrganization = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof OrganizationFormSchema> & { id: string },
    ) => {
      const { id, period, upperOrganizationIds, ...payload } =
        organizationFormValuesParser
          .extend({
            id: z.string().uuid(),
          })
          .parse(values);
      return await axiosPatch(`/api/organizations/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
        upperId: upperOrganizationIds?.at(-1) ?? null,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateListMap.organizations().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};

export const useDownloadOrganizationListCsv = () => {
  return useMutation({
    mutationFn: async () => {
      return await axiosPost('/api/organizations/csv/', null);
    },
  });
};
